import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { api } from '../../services/api';
import MediaUpload from '../../components/property/MediaUpload';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

// --- Constants ---

const PROPERTY_TYPES = ['house', 'apartment', 'condo', 'lot', 'parcel', 'office', 'warehouse', 'other'] as const;
const OPERATION_TYPES = ['sale', 'rent', 'both'] as const;

const AMENITIES = ['pool', 'garden', 'security', 'elevator', 'balcony', 'terrace', 'laundry', 'parking', 'gym', 'bbq'] as const;

const PROVINCES = [
  { value: 'buenos_aires', label: 'Buenos Aires' },
  { value: 'caba', label: 'CABA' },
  { value: 'catamarca', label: 'Catamarca' },
  { value: 'chaco', label: 'Chaco' },
  { value: 'chubut', label: 'Chubut' },
  { value: 'cordoba', label: 'Córdoba' },
  { value: 'corrientes', label: 'Corrientes' },
  { value: 'entre_rios', label: 'Entre Ríos' },
  { value: 'formosa', label: 'Formosa' },
  { value: 'jujuy', label: 'Jujuy' },
  { value: 'la_pampa', label: 'La Pampa' },
  { value: 'la_rioja', label: 'La Rioja' },
  { value: 'mendoza', label: 'Mendoza' },
  { value: 'misiones', label: 'Misiones' },
  { value: 'neuquen', label: 'Neuquén' },
  { value: 'rio_negro', label: 'Río Negro' },
  { value: 'salta', label: 'Salta' },
  { value: 'san_juan', label: 'San Juan' },
  { value: 'san_luis', label: 'San Luis' },
  { value: 'santa_cruz', label: 'Santa Cruz' },
  { value: 'santa_fe', label: 'Santa Fe' },
  { value: 'santiago_del_estero', label: 'Santiago del Estero' },
  { value: 'tierra_del_fuego', label: 'Tierra del Fuego' },
  { value: 'tucuman', label: 'Tucumán' },
] as const;

// --- Quill toolbar config ---

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean'],
  ],
};

const QUILL_FORMATS = [
  'header',
  'bold',
  'italic',
  'underline',
  'list',
  'link',
];

// --- Zod Schema ---

const propertySchema = z.object({
  // Step 1 - Basic
  title: z.string().min(5).max(200),
  description: z.string().max(5000).optional().or(z.literal('')),
  property_type: z.enum(PROPERTY_TYPES),
  operation_type: z.enum(OPERATION_TYPES),
  // Step 2 - Location
  address: z.string().min(1).max(500).optional().or(z.literal('')),
  city: z.string().min(1).max(200).optional().or(z.literal('')),
  province: z.string().max(100).optional().or(z.literal('')),
  zip_code: z.string().max(20).optional().or(z.literal('')),
  // Step 3 - Details
  price: z.coerce.number().min(0).optional().or(z.literal(0)),
  currency: z.enum(['ARS', 'USD']).default('ARS'),
  expenses: z.coerce.number().min(0).optional().or(z.literal(0)),
  surface_total: z.coerce.number().min(0).optional().or(z.literal(0)),
  surface_covered: z.coerce.number().min(0).optional().or(z.literal(0)),
  rooms: z.coerce.number().int().min(0).optional().or(z.literal(0)),
  bedrooms: z.coerce.number().int().min(0).optional().or(z.literal(0)),
  bathrooms: z.coerce.number().int().min(0).optional().or(z.literal(0)),
  garage: z.coerce.number().int().min(0).optional().or(z.literal(0)),
  floor: z.string().max(20).optional().or(z.literal('')),
  amenities: z.array(z.string()).default([]),
});

type PropertyFormData = z.infer<typeof propertySchema>;
type PropertyFormInput = z.input<typeof propertySchema>;

interface PropertyApiResponse {
  data: PropertyFormData & { id: number; status: string };
}

// --- Steps ---

const STEP_KEYS = ['basic', 'location', 'details', 'media'] as const;

// Fields per step (for partial validation / highlighting)
const STEP_FIELDS: Record<string, (keyof PropertyFormInput)[]> = {
  basic: ['title', 'description', 'property_type', 'operation_type'],
  location: ['address', 'city', 'province', 'zip_code'],
  details: ['price', 'currency', 'expenses', 'surface_total', 'surface_covered', 'rooms', 'bedrooms', 'bathrooms', 'garage', 'floor', 'amenities'],
  media: [],
};

// Required fields for publish
const PUBLISH_REQUIRED_FIELDS: (keyof PropertyFormData)[] = ['title', 'property_type', 'operation_type'];

// --- Component ---

export default function PropertyFormPage(): React.ReactElement {
  const { t } = useTranslation('properties');
  const { t: tCommon } = useTranslation('common');
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const isEdit = !!id;

  const [activeStep, setActiveStep] = useState(0);
  const [propertyId, setPropertyId] = useState<number | null>(id ? Number(id) : null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // --- Auto-dismiss snackbar ---
  useEffect(() => {
    if (snackbar.open) {
      const timer = setTimeout(() => setSnackbar((s) => ({ ...s, open: false })), 3000);
      return () => clearTimeout(timer);
    }
  }, [snackbar.open]);

  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    trigger,
    getValues,
    formState: { errors },
  } = useForm<PropertyFormInput, unknown, PropertyFormData>({
    resolver: zodResolver(propertySchema),
    defaultValues: {
      title: '',
      description: '',
      property_type: undefined,
      operation_type: undefined,
      address: '',
      city: '',
      province: '',
      zip_code: '',
      price: 0,
      currency: 'ARS',
      expenses: 0,
      surface_total: 0,
      surface_covered: 0,
      rooms: 0,
      bedrooms: 0,
      bathrooms: 0,
      garage: 0,
      floor: '',
      amenities: [],
    },
  });

  const watchedAmenities = watch('amenities');

  // Fetch existing property for edit mode
  const { data: existingProperty, isLoading: isLoadingProperty } = useQuery({
    queryKey: ['property', id],
    queryFn: () => api.get<PropertyApiResponse>(`/v1/properties/${id}`),
    enabled: isEdit,
  });

  // Populate form with existing data
  useEffect(() => {
    if (existingProperty?.data) {
      const p = existingProperty.data;
      reset({
        title: p.title || '',
        description: p.description || '',
        property_type: p.property_type,
        operation_type: p.operation_type,
        address: p.address || '',
        city: p.city || '',
        province: p.province || '',
        zip_code: p.zip_code || '',
        price: p.price || 0,
        currency: p.currency || 'ARS',
        expenses: p.expenses || 0,
        surface_total: p.surface_total || 0,
        surface_covered: p.surface_covered || 0,
        rooms: p.rooms || 0,
        bedrooms: p.bedrooms || 0,
        bathrooms: p.bathrooms || 0,
        garage: p.garage || 0,
        floor: p.floor || '',
        amenities: p.amenities || [],
      });
    }
  }, [existingProperty, reset]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: PropertyFormData & { status?: string }) =>
      api.post<PropertyApiResponse>('/v1/properties', data),
    onSuccess: (response) => {
      const newId = response.data.id;
      setPropertyId(newId);
      queryClient.invalidateQueries({ queryKey: ['properties'] });
      return newId;
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: PropertyFormData & { status?: string }) =>
      api.patch<PropertyApiResponse>(`/v1/properties/${propertyId}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['properties'] });
      queryClient.invalidateQueries({ queryKey: ['property', id] });
    },
  });

  const isSaving = createMutation.isPending || updateMutation.isPending;

  const saveDraft = async (): Promise<void> => {
    const data = getValues() as unknown as PropertyFormData;
    try {
      if (propertyId) {
        await updateMutation.mutateAsync({ ...data, status: 'draft' });
        setSnackbar({ open: true, message: t('form.messages.draftSaved'), severity: 'success' });
      } else {
        const response = await createMutation.mutateAsync({ ...data, status: 'draft' });
        setSnackbar({ open: true, message: t('form.messages.draftSaved'), severity: 'success' });
        // Navigate to edit URL so we don't lose the ID on refresh
        navigate(`/app/properties/${response.data.id}/edit`, { replace: true });
      }
    } catch {
      setSnackbar({ open: true, message: t('form.messages.error'), severity: 'error' });
    }
  };

  const publish = async (data: PropertyFormData): Promise<void> => {
    try {
      if (propertyId) {
        await updateMutation.mutateAsync({ ...data, status: 'active' });
        setSnackbar({ open: true, message: t('form.messages.published'), severity: 'success' });
      } else {
        await createMutation.mutateAsync({ ...data, status: 'active' });
        setSnackbar({ open: true, message: t('form.messages.published'), severity: 'success' });
      }
      // Navigate back to list after a short delay
      setTimeout(() => navigate('/app/properties'), 1500);
    } catch {
      setSnackbar({ open: true, message: t('form.messages.error'), severity: 'error' });
    }
  };

  const handleNext = async (): Promise<void> => {
    const stepFields = STEP_FIELDS[STEP_KEYS[activeStep]];
    const valid = stepFields.length === 0 || (await trigger(stepFields));
    if (valid) {
      setActiveStep((prev) => Math.min(prev + 1, STEP_KEYS.length - 1));
    }
  };

  const handleBack = (): void => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const canPublish = (): boolean => {
    const values = getValues() as unknown as PropertyFormData;
    return PUBLISH_REQUIRED_FIELDS.every((field) => {
      const val = values[field];
      return val !== undefined && val !== '' && val !== null;
    });
  };

  if (isEdit && isLoadingProperty) {
    return (
      <div className="flex justify-center p-8">
        <span className="loading loading-spinner loading-lg" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button className="btn btn-ghost" onClick={() => navigate('/app/properties')}>
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
          </svg>
          {tCommon('buttons.back')}
        </button>
        <h1 className="text-3xl font-bold">
          {isEdit ? t('editTitle') : t('createTitle')}
        </h1>
      </div>

      {/* DaisyUI Steps */}
      <ul className="steps steps-horizontal w-full mb-8">
        {STEP_KEYS.map((key, index) => (
          <li
            key={key}
            className={`step ${index <= activeStep ? 'step-primary' : ''} cursor-pointer`}
            onClick={() => {
              if (index < activeStep) setActiveStep(index);
            }}
          >
            {t(`form.steps.${key}`)}
          </li>
        ))}
      </ul>

      {/* Card with form */}
      <div className="card bg-base-100 shadow-sm max-w-3xl mx-auto">
        <div className="card-body">
          <form onSubmit={handleSubmit(publish)} noValidate>
            {/* Step 1: Basic */}
            {activeStep === 0 && (
              <div className="flex flex-col gap-4">
                <div className="form-control w-full">
                  <label className="label"><span className="label-text">{t('form.basic.title')}</span></label>
                  <input
                    type="text"
                    className={`input input-bordered w-full ${errors.title ? 'input-error' : ''}`}
                    placeholder={t('form.basic.titlePlaceholder')}
                    {...register('title')}
                  />
                  {errors.title && (
                    <label className="label"><span className="label-text-alt text-error">{t('form.validation.titleRequired')}</span></label>
                  )}
                </div>

                <div className="form-control w-full">
                  <label className="label"><span className="label-text">{t('form.basic.description')}</span></label>
                  <Controller
                    name="description"
                    control={control}
                    render={({ field }) => (
                      <ReactQuill
                        theme="snow"
                        value={field.value || ''}
                        onChange={field.onChange}
                        modules={QUILL_MODULES}
                        formats={QUILL_FORMATS}
                        placeholder={t('form.basic.descriptionPlaceholder')}
                        className={errors.description ? 'border border-error rounded-lg' : ''}
                      />
                    )}
                  />
                  {errors.description && (
                    <label className="label"><span className="label-text-alt text-error">{errors.description.message}</span></label>
                  )}
                </div>

                <Controller
                  name="property_type"
                  control={control}
                  render={({ field }) => (
                    <div className="form-control w-full">
                      <label className="label"><span className="label-text">{t('form.basic.propertyType')}</span></label>
                      <select
                        className={`select select-bordered w-full ${errors.property_type ? 'select-error' : ''}`}
                        value={field.value || ''}
                        onChange={(e) => field.onChange(e.target.value)}
                      >
                        <option value="" disabled>{t('form.basic.propertyType')}</option>
                        {PROPERTY_TYPES.map((type) => (
                          <option key={type} value={type}>
                            {t(`propertyType.${type}`)}
                          </option>
                        ))}
                      </select>
                      {errors.property_type && (
                        <label className="label"><span className="label-text-alt text-error">{t('form.validation.propertyTypeRequired')}</span></label>
                      )}
                    </div>
                  )}
                />

                <Controller
                  name="operation_type"
                  control={control}
                  render={({ field }) => (
                    <div className="form-control w-full">
                      <label className="label"><span className="label-text">{t('form.basic.operationType')}</span></label>
                      <select
                        className={`select select-bordered w-full ${errors.operation_type ? 'select-error' : ''}`}
                        value={field.value || ''}
                        onChange={(e) => field.onChange(e.target.value)}
                      >
                        <option value="" disabled>{t('form.basic.operationType')}</option>
                        {OPERATION_TYPES.map((op) => (
                          <option key={op} value={op}>
                            {t(`operationType.${op}`)}
                          </option>
                        ))}
                      </select>
                      {errors.operation_type && (
                        <label className="label"><span className="label-text-alt text-error">{t('form.validation.operationTypeRequired')}</span></label>
                      )}
                    </div>
                  )}
                />
              </div>
            )}

            {/* Step 2: Location */}
            {activeStep === 1 && (
              <div className="flex flex-col gap-4">
                <div className="form-control w-full">
                  <label className="label"><span className="label-text">{t('form.location.address')}</span></label>
                  <input
                    type="text"
                    className={`input input-bordered w-full ${errors.address ? 'input-error' : ''}`}
                    placeholder={t('form.location.addressPlaceholder')}
                    {...register('address')}
                  />
                  {errors.address && (
                    <label className="label"><span className="label-text-alt text-error">{errors.address.message}</span></label>
                  )}
                </div>

                <div className="form-control w-full">
                  <label className="label"><span className="label-text">{t('form.location.city')}</span></label>
                  <input
                    type="text"
                    className={`input input-bordered w-full ${errors.city ? 'input-error' : ''}`}
                    placeholder={t('form.location.cityPlaceholder')}
                    {...register('city')}
                  />
                  {errors.city && (
                    <label className="label"><span className="label-text-alt text-error">{errors.city.message}</span></label>
                  )}
                </div>

                <Controller
                  name="province"
                  control={control}
                  render={({ field }) => (
                    <div className="form-control w-full">
                      <label className="label"><span className="label-text">{t('form.location.province')}</span></label>
                      <select
                        className={`select select-bordered w-full ${errors.province ? 'select-error' : ''}`}
                        value={field.value || ''}
                        onChange={(e) => field.onChange(e.target.value)}
                      >
                        <option value="">{t('list.filters.all', { defaultValue: '-' })}</option>
                        {PROVINCES.map((prov) => (
                          <option key={prov.value} value={prov.value}>
                            {t(`provinces.${prov.value}`, { defaultValue: prov.label })}
                          </option>
                        ))}
                      </select>
                      {errors.province && (
                        <label className="label"><span className="label-text-alt text-error">{t('form.validation.provinceRequired')}</span></label>
                      )}
                    </div>
                  )}
                />

                <div className="form-control w-full">
                  <label className="label"><span className="label-text">{t('form.location.zipCode')}</span></label>
                  <input
                    type="text"
                    className={`input input-bordered w-full ${errors.zip_code ? 'input-error' : ''}`}
                    placeholder={t('form.location.zipCodePlaceholder')}
                    {...register('zip_code')}
                  />
                  {errors.zip_code && (
                    <label className="label"><span className="label-text-alt text-error">{errors.zip_code.message}</span></label>
                  )}
                </div>
              </div>
            )}

            {/* Step 3: Details */}
            {activeStep === 2 && (
              <div className="flex flex-col gap-4">
                {/* Price row */}
                <div className="flex gap-4">
                  <div className="form-control flex-1">
                    <label className="label"><span className="label-text">{t('form.details.price')}</span></label>
                    <label className="input input-bordered flex items-center gap-2">
                      <span className="text-base-content/50">$</span>
                      <input
                        type="number"
                        className="grow"
                        {...register('price')}
                      />
                    </label>
                    {errors.price && (
                      <label className="label"><span className="label-text-alt text-error">{errors.price.message}</span></label>
                    )}
                  </div>
                  <Controller
                    name="currency"
                    control={control}
                    render={({ field }) => (
                      <div className="form-control">
                        <label className="label"><span className="label-text">&nbsp;</span></label>
                        <div className="join">
                          <button
                            type="button"
                            className={`join-item btn ${field.value === 'ARS' ? 'btn-primary' : 'btn-outline'}`}
                            onClick={() => field.onChange('ARS')}
                          >
                            ARS
                          </button>
                          <button
                            type="button"
                            className={`join-item btn ${field.value === 'USD' ? 'btn-primary' : 'btn-outline'}`}
                            onClick={() => field.onChange('USD')}
                          >
                            USD
                          </button>
                        </div>
                      </div>
                    )}
                  />
                </div>

                <div className="form-control w-full">
                  <label className="label"><span className="label-text">{t('form.details.expenses')}</span></label>
                  <label className="input input-bordered flex items-center gap-2">
                    <span className="text-base-content/50">$</span>
                    <input
                      type="number"
                      className="grow"
                      {...register('expenses')}
                    />
                  </label>
                  {errors.expenses && (
                    <label className="label"><span className="label-text-alt text-error">{errors.expenses.message}</span></label>
                  )}
                </div>

                {/* Surface row */}
                <div className="flex gap-4">
                  <div className="form-control flex-1">
                    <label className="label"><span className="label-text">{t('form.details.surfaceTotal')}</span></label>
                    <input
                      type="number"
                      className={`input input-bordered w-full ${errors.surface_total ? 'input-error' : ''}`}
                      {...register('surface_total')}
                    />
                    {errors.surface_total && (
                      <label className="label"><span className="label-text-alt text-error">{errors.surface_total.message}</span></label>
                    )}
                  </div>
                  <div className="form-control flex-1">
                    <label className="label"><span className="label-text">{t('form.details.surfaceCovered')}</span></label>
                    <input
                      type="number"
                      className={`input input-bordered w-full ${errors.surface_covered ? 'input-error' : ''}`}
                      {...register('surface_covered')}
                    />
                    {errors.surface_covered && (
                      <label className="label"><span className="label-text-alt text-error">{errors.surface_covered.message}</span></label>
                    )}
                  </div>
                </div>

                {/* Rooms row */}
                <div className="flex flex-wrap gap-4">
                  <div className="form-control flex-1 min-w-[100px]">
                    <label className="label"><span className="label-text">{t('form.details.rooms')}</span></label>
                    <input
                      type="number"
                      className={`input input-bordered w-full ${errors.rooms ? 'input-error' : ''}`}
                      {...register('rooms')}
                    />
                  </div>
                  <div className="form-control flex-1 min-w-[100px]">
                    <label className="label"><span className="label-text">{t('form.details.bedrooms')}</span></label>
                    <input
                      type="number"
                      className={`input input-bordered w-full ${errors.bedrooms ? 'input-error' : ''}`}
                      {...register('bedrooms')}
                    />
                  </div>
                  <div className="form-control flex-1 min-w-[100px]">
                    <label className="label"><span className="label-text">{t('form.details.bathrooms')}</span></label>
                    <input
                      type="number"
                      className={`input input-bordered w-full ${errors.bathrooms ? 'input-error' : ''}`}
                      {...register('bathrooms')}
                    />
                  </div>
                </div>

                {/* Garage & Floor row */}
                <div className="flex gap-4">
                  <div className="form-control flex-1">
                    <label className="label"><span className="label-text">{t('form.details.garage')}</span></label>
                    <input
                      type="number"
                      className={`input input-bordered w-full ${errors.garage ? 'input-error' : ''}`}
                      {...register('garage')}
                    />
                  </div>
                  <div className="form-control flex-1">
                    <label className="label"><span className="label-text">{t('form.details.floor')}</span></label>
                    <input
                      type="text"
                      className={`input input-bordered w-full ${errors.floor ? 'input-error' : ''}`}
                      {...register('floor')}
                    />
                  </div>
                </div>

                {/* Amenities */}
                <div>
                  <label className="label"><span className="label-text font-medium">{t('form.details.amenities')}</span></label>
                  <Controller
                    name="amenities"
                    control={control}
                    render={({ field }) => (
                      <div className="flex flex-wrap gap-2">
                        {AMENITIES.map((amenity) => {
                          const isSelected = (watchedAmenities || []).includes(amenity);
                          return (
                            <button
                              key={amenity}
                              type="button"
                              className={`badge badge-lg cursor-pointer ${isSelected ? 'badge-primary' : 'badge-outline'}`}
                              onClick={() => {
                                const current = field.value || [];
                                const updated = isSelected
                                  ? current.filter((a) => a !== amenity)
                                  : [...current, amenity];
                                field.onChange(updated);
                              }}
                            >
                              {t(`amenities.${amenity}`)}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  />
                </div>
              </div>
            )}

            {/* Step 4: Media */}
            {activeStep === 3 && (
              <MediaUpload propertyId={propertyId} />
            )}

            {/* Navigation buttons */}
            <div className="flex justify-between mt-8 pt-4 border-t border-base-200">
              <div className="flex gap-2">
                {activeStep > 0 && (
                  <button type="button" className="btn btn-ghost" onClick={handleBack}>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                    </svg>
                    {t('form.buttons.previous')}
                  </button>
                )}
              </div>

              <div className="flex gap-2">
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={saveDraft}
                  disabled={isSaving}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M7.707 10.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V6h5a2 2 0 012 2v7a2 2 0 01-2 2H4a2 2 0 01-2-2V8a2 2 0 012-2h5v5.586l-1.293-1.293zM9 4a1 1 0 012 0v2H9V4z" />
                  </svg>
                  {isSaving ? t('form.buttons.saving') : t('form.buttons.saveDraft')}
                </button>

                {activeStep < STEP_KEYS.length - 1 && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleNext}
                  >
                    {t('form.buttons.next')}
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                )}

                {activeStep === STEP_KEYS.length - 1 && (
                  <button
                    type="submit"
                    className="btn btn-success"
                    disabled={isSaving || !canPublish()}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 6.707a1 1 0 010-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L7.707 6.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                    {isSaving ? t('form.buttons.publishing') : t('form.buttons.publish')}
                  </button>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Toast / Snackbar */}
      {snackbar.open && (
        <div className="toast toast-end toast-bottom z-50">
          <div className={`alert ${snackbar.severity === 'success' ? 'alert-success' : 'alert-error'}`}>
            <span>{snackbar.message}</span>
            <button className="btn btn-ghost btn-xs" onClick={() => setSnackbar((s) => ({ ...s, open: false }))}>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
